import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useCoreDispatch } from '../../../../store/CoreStore';
import { useConfigSlice } from '../../../../store/slices/config/selectors';
import { useHeaderSlice } from '../../../../store/slices/header/selectors';
import { headerActions } from '../../../../store/slices/header/slice';
import { useNickNameInfoSlice } from '../../../../store/slices/nickname/selectors';
import { nickNameInfoActions } from '../../../../store/slices/nickname/slice';
import { NavPages } from '../../navPages/NavPagesConnected';
import { NavPagesTitleConnected } from '../../navPages/NavPagesTitleConnected';
import { DrawerSliderConnected } from '../drawerSlider/DrawerSliderConnected';
import { NavDrawer } from '../NavDrawer';
import type { ClickCallback } from '../types';

export const NavDrawerControlConnected: FC = () => {
  const dispatch = useCoreDispatch();
  const open = useHeaderSlice.getNavOpen();
  const isNickNameEditOpen = useNickNameInfoSlice.getIsNickNameEditOpen();
  const isExpanded = useHeaderSlice.getNavExpanded();
  const isForcedExpanded = useHeaderSlice.getNavForcedExpanded();
  const navRouteLevel = useHeaderSlice.getNavRouteLevel();
  const showTableLimitsPopup = Boolean(useConfigSlice.jurisdiction()?.showTableLimitsPopup);
  const [inTransition, setInTransition] = useState(false);

  useEffect(() => {
    if (showTableLimitsPopup) {
      dispatch(headerActions.pushNavRoute(NavPages.HOME));
      dispatch(headerActions.pushNavRoute(NavPages.BET_LIMITS));
    }
  }, [showTableLimitsPopup]);

  const handleNicknameEditState = useCallback(() => {
    if (isNickNameEditOpen) {
      dispatch(nickNameInfoActions.setIsNickNameEditOpen(false));
      return true; // indicate that nickname edit was handled
    }

    return false;
  }, [isNickNameEditOpen]);

  const handleBack: ClickCallback<HTMLButtonElement> = useCallback(
    (event) => {
      event?.stopPropagation();

      if (!handleNicknameEditState()) {
        dispatch(headerActions.popNavRoute());
      }
    },
    [handleNicknameEditState]
  );

  const handleClose: ClickCallback<HTMLElement> = useCallback(
    (event) => {
      event?.stopPropagation();
      handleNicknameEditState();
      dispatch(headerActions.setNavRoute([]));
    },
    [handleNicknameEditState]
  );

  const handleExpand = useCallback((expanded: boolean) => {
    dispatch(headerActions.setNavExpanded(expanded));
  }, []);

  useEffect(() => {
    setInTransition(true);
  }, [open]);

  return (
    <NavDrawer
      open={open}
      title={<NavPagesTitleConnected />}
      handleBack={handleBack}
      handleClose={handleClose}
      handleExpand={handleExpand}
      backButtonVisible={navRouteLevel >= 2}
      isExpanded={isExpanded}
      isForcedExpanded={isForcedExpanded}
      onTransitionEnd={() => setInTransition(false)}
    >
      <DrawerSliderConnected disableAnimation={inTransition} />
    </NavDrawer>
  );
};
